import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEmailValidationClient } from "@contexts/EmailValidationClient.context";
import { renderRoute, routeHome, routeLogin } from "@navigation/typedRoutes";
import { setPendingEmailValidation } from "@ps-bridge/services/pendingEmailValidation";
import { useOnRouteChangeTask, useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { ioEffect } from "@utils/effects/branded";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";
import * as Option from "@utils/Option";
import { getParamOption } from "@utils/searchParams";
import { ok } from "@ps-bridge/ok";
import { mkNavigate, mkStorageRemove, mkTaskRunner } from "@ps-bridge/capabilities";
import EmailValidationView from "@view/authentication/EmailValidation.view";
import { buildEmailValidationViewProps } from "@ps-bridge/emailValidation.program";
import { mkEmailValidationController } from "@ps-bridge/controllers/emailValidation";
import type { EmailValidationKernel, EmailValidationStatus, StateUpdate } from "@ps-bridge/contracts/emailValidation";
import type { OptionTs, TaskTs } from "@ps-bridge/interop";

const EmailValidationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailValidationClient = useEmailValidationClient();
  const runTaskWithError = useRunTaskWithError();

  const tokenOption = useMemo<OptionTs<string>>(() => {
    const params = new URLSearchParams(location.search);
    return getParamOption(params, "token");
  }, [location.search]);

  const runTaskWithErrorIo = useMemo(
    () => (task: TaskTs<{ readonly success: boolean }>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );
  const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);

  const statusHandle = useIoState<EmailValidationStatus>("idle");
  const errorMessageHandle = useIoOptionState<string>(Option.none());
  const pendingValidationUpdate = useMemo<StateUpdate<boolean>>(
    () =>
      ((value: boolean) =>
        ioEffect(() => {
          setPendingEmailValidation(value);
          return ok;
        })) as StateUpdate<boolean>,
    []
  );
  const storageRemove = useMemo(
    () =>
      mkStorageRemove((key) =>
        ioEffect(() => {
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.removeItem(key);
          }
          return ok;
        })
      ),
    []
  );
  const navigateAction = useMemo(
    () =>
      mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        })
      ),
    [navigate]
  );

  const controller = useMemo(
    () =>
      mkEmailValidationController({
        status: { read: statusHandle.read, set: statusHandle.set },
        errorMessage: {
          read: errorMessageHandle.read,
          setSome: errorMessageHandle.setSome,
          setMaybe: errorMessageHandle.setMaybe,
        },
        client: emailValidationClient,
        pendingValidation: pendingValidationUpdate,
        storageRemove,
      }),
    [
      emailValidationClient,
      errorMessageHandle.read,
      errorMessageHandle.setMaybe,
      errorMessageHandle.setSome,
      pendingValidationUpdate,
      statusHandle.read,
      statusHandle.set,
      storageRemove,
    ]
  );
  const kernel: EmailValidationKernel = useMemo(
    () => ({
      controller,
      status: { read: statusHandle.read, set: statusHandle.set },
      errorMessage: {
        read: errorMessageHandle.read,
        setSome: errorMessageHandle.setSome,
        setMaybe: errorMessageHandle.setMaybe,
      },
      client: emailValidationClient,
      navigate: navigateAction,
      pendingValidation: pendingValidationUpdate,
      storageRemove,
      taskRunner,
    }),
    [
      controller,
      emailValidationClient,
      errorMessageHandle.read,
      errorMessageHandle.setMaybe,
      errorMessageHandle.setSome,
      navigateAction,
      pendingValidationUpdate,
      statusHandle.read,
      statusHandle.set,
      storageRemove,
      taskRunner,
    ]
  );
  const viewProps = useMemo(
    () =>
      buildEmailValidationViewProps({
        kernel,
        homeRoute: routeHome,
        loginRoute: routeLogin,
      }),
    [kernel]
  );
  useOnRouteChangeTask(tokenOption, viewProps.onTokenRouteChange);

  return <EmailValidationView {...viewProps} />;
};

export default EmailValidationPage;
